import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import useAuthStore from '../store/authStore';
import Icon from './Icon';
import CenterSwitcher from './CenterSwitcher';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import useNavMenuItems from '../config/navMenu';
import { userDisplayName, userInitials } from '../utils/userDisplay';

/**
 * Side Drawer (Hamburger Menu)
 * Opens from the side with profile, secondary navigation, utilities, and logout
 */
export default function SideDrawer({ isOpen, onClose }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const location = useLocation();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';
  const [isDark, setIsDark] = useState(document.documentElement.getAttribute('data-theme') === 'dark');

  const toggleDarkMode = () => {
    const newTheme = isDark ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
    setIsDark(!isDark);
  };

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Same list the desktop sidebar renders, minus the desktop-only entries.
  // Previously this component kept its own hand-written copy, which drifted
  // and left whole pages unreachable on a phone.
  const baseNavItems = useNavMenuItems('mobile');
  // Teacher/worker menus deliberately omit Settings on desktop, but on a phone
  // the drawer is the only way in — the bottom nav used to carry it. Append it
  // when the role's own list doesn't already end with it.
  const navItems = baseNavItems.some((i) => i.path === '/settings')
    ? baseNavItems
    : [...baseNavItems, { labelKey: 'sidebar.settings', iconName: 'settings', path: '/settings' }];
  const navLabel = (item) =>
    t(item.labelKey, item.labelFallback ? { defaultValue: item.labelFallback } : undefined);
  const activePath = navItems
    .filter((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0]?.path;
  const isActive = (path) => path === activePath;

  const handleNavigation = (path) => {
    navigate(path);
    onClose();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    onClose();
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

  // Get user initials for avatar
  const getInitials = () => userInitials(user);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        className="side-drawer-backdrop"
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1030,
          animation: 'fadeIn 0.3s ease-out'
        }}
      />

      {/* Drawer */}
      <div
        className="side-drawer"
        style={{
          position: 'fixed',
          top: 0,
          [isRTL ? 'left' : 'right']: 0,
          bottom: 0,
          width: '80%',
          maxWidth: '320px',
          backgroundColor: 'var(--color-surface)',
          boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.1)',
          zIndex: 1035,
          display: 'flex',
          flexDirection: 'column',
          animation: isRTL ? 'slideInLeft 0.3s ease-out' : 'slideInRight 0.3s ease-out',
          direction: isRTL ? 'rtl' : 'ltr'
        }}
      >
        {/* Section A: Header (The "Me" Zone) */}
        <div style={{
          padding: '16px 12px',
          backgroundColor: 'var(--mobile-primary, #10B981)',
          color: 'white'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '8px'
          }}>
            {/* Avatar */}
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              fontWeight: '700',
              color: 'white',
              border: '1.5px solid rgba(255, 255, 255, 0.3)'
            }}>
              {getInitials()}
            </div>

            {/* User Info */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '15px',
                fontWeight: '700',
                marginBottom: '2px'
              }}>
                {userDisplayName(user, t('header.defaultUser'))}
              </div>
              <div style={{
                fontSize: '12px',
                opacity: 0.9
              }}>
                {user?.role === 'teacher' ? t('header.manager') : t('header.centerManager')}
              </div>
            </div>
          </div>

          {/* Edit Profile Link */}
          <button
            onClick={() => handleNavigation('/account')}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 12px',
              color: 'white',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'center'
            }}
          >
            {t('student.editProfile')}
          </button>
        </div>

        {/* Center switcher — the desktop Header (which hosts it) is never
            mounted on phone/tablet, so without this a multi-center user simply
            cannot change centers on mobile. Renders nothing for single-center
            users, exactly like the desktop one. */}
        <div style={{ padding: '10px 14px 0' }}>
          <CenterSwitcher />
        </div>

        {/* Section B: Navigation (synced with desktop Sidebar) */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 0'
        }}>
          <nav>
            {/* Navigation label */}
            <div style={{ padding: '6px 14px 2px', fontSize: '11px', fontWeight: '600', color: 'var(--color-text-tertiary, #9CA3AF)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {t('sidebar.navigation', { defaultValue: 'Navigation' })}
            </div>

            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className={`side-drawer-item${isActive(item.path) ? ' active' : ''}`}
              >
                <Icon name={item.iconName} />
                <span>{navLabel(item)}</span>
              </button>
            ))}
          </nav>

          {/* Section C: System Utilities */}
          <div style={{
            borderTop: '1px solid var(--color-border, #E5E7EB)',
            marginTop: '4px',
            paddingTop: '4px'
          }}>
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="side-drawer-item"
            >
              {isDark ? <Sun size={20} color="var(--color-text-secondary, #6B7280)" /> : <Moon size={20} color="var(--color-text-secondary, #6B7280)" />}
              <span>{isDark
                  ? t('header.lightMode', { defaultValue: 'Light Mode' })
                  : t('header.darkMode', { defaultValue: 'Dark Mode' })}</span>
            </button>

            {/* Language Selector */}
            <div style={{ padding: '4px 14px' }}>
              <div style={{
                fontSize: '11px',
                fontWeight: '600',
                color: 'var(--color-text-tertiary, #9CA3AF)',
                marginBottom: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                {t('header.language')}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                {['en', 'he', 'ar'].map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    style={{
                      flex: 1,
                      padding: '5px',
                      borderRadius: '6px',
                      border: `2px solid ${i18n.language === lang ? 'var(--color-primary, #10B981)' : 'var(--color-border, #E5E7EB)'}`,
                      backgroundColor: i18n.language === lang ? 'rgba(16, 185, 129, 0.1)' : 'transparent',
                      color: i18n.language === lang ? 'var(--color-primary, #10B981)' : 'var(--color-text-secondary, #6B7280)',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    {lang === 'en' ? 'EN' : lang === 'he' ? 'עב' : 'ع'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Section D: Footer (Logout & Version) */}
        <div style={{
          borderTop: '1px solid var(--mobile-border-light, #E5E7EB)',
          padding: '10px 14px'
        }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <Icon name="logout" />
            <span>{t('sidebar.logout')}</span>
          </button>

          <div style={{
            textAlign: 'center',
            fontSize: '11px',
            color: 'var(--mobile-text-secondary, #6B7280)',
            marginTop: '6px'
          }}>
            v2.1.0
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }

        @keyframes slideInLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }

        .side-drawer-item {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 7px 14px;
          border: none;
          background: none;
          color: var(--color-text-primary, #1F2937);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          text-align: ${isRTL ? 'right' : 'left'};
          transition: background-color 0.15s;
          border-radius: 0;
        }

        .side-drawer-item:hover {
          background-color: var(--color-hover, rgba(0, 0, 0, 0.05));
        }

        .side-drawer-item:active {
          background-color: var(--color-hover, rgba(0, 0, 0, 0.08));
        }

        .side-drawer-item.active {
          background-color: var(--color-hover, rgba(16, 185, 129, 0.08));
          color: var(--color-primary, #10B981);
          font-weight: 600;
        }

        .side-drawer-item.active .material-icons {
          color: var(--color-primary, #10B981);
        }

        .side-drawer-item .material-icons {
          font-size: 20px;
          color: var(--color-text-secondary, #6B7280);
        }
      `}</style>
    </>,
    document.body
  );
}
